import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";

import { useLocation } from "react-router-dom";
import { Form, Formik, useFormikContext } from "formik";
import { IoMdLock, IoMdUnlock, IoIosHourglass } from "react-icons/io";
import { GoCheckCircleFill } from "react-icons/go";
import { HiOutlineViewGridAdd } from "react-icons/hi";
import { FaRegEdit } from "react-icons/fa";
import Swal from "sweetalert2";

import {
  Button,
  Flex,
  Text,
  Box,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  HStack,
  Tooltip,
  Icon,
  useBreakpointValue,
} from "@chakra-ui/react";

import * as Yup from "yup";

import pt from "date-fns/locale/pt";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import RelatorioService from "../../../main/services/RelatorioService";

import { useAuth } from "react-oidc-context";
import { IoHelpCircle } from "react-icons/io5";
import { format, isAfter, isEqual, isValid, parse } from "date-fns";
import { UniversoConsultaAba } from "../../pages/Relatorios/CriancaAdolescenteGeral/Abas/UniversoConsultaAba";
import { DadosBasicosAba } from "../../pages/Relatorios/CriancaAdolescenteGeral/Abas/DadosBasicosAba";
import { LocalizacaoServicoAba } from "../../pages/Relatorios/ServicoAcolhimento/Abas/LocalizacaoServicoAba";
import { OrgaosAba } from "../../pages/Relatorios/ServicoAcolhimento/Abas/OrgaosAba";
import { ReferenciaFamiliarAba } from "../../pages/Relatorios/CriancaAdolescenteGeral/Abas/ReferenciaFamiliarAba";
import { dashboardFunc } from "../../../helpers/Funcionalidades";
import { SaudeAba } from "../../pages/Relatorios/CriancaAdolescenteGeral/Abas/SaudeAba";
import { EducacaoAba } from "../../pages/Relatorios/CriancaAdolescenteGeral/Abas/EducacaoAba";
import { ProfissionalizacaoAba } from "../../pages/Relatorios/CriancaAdolescenteGeral/Abas/ProfissionalizacaoAba";
import { VisitaAba } from "../../pages/Relatorios/CriancaAdolescenteGeral/Abas/VisitaAba";
import { SituacaoJuridicaAba } from "../../pages/Relatorios/CriancaAdolescenteGeral/Abas/SituacaoJuridicaAba";
import { SituacaoAdocaoAba } from "../../pages/Relatorios/CriancaAdolescenteGeral/Abas/SituacaoAdocaoAba";
import { ProgramaApadrinhementoAba } from "../../pages/Relatorios/CriancaAdolescenteGeral/Abas/ProgramaApadrinhementoAba";
import SemRegistros from "../SemRegistros";
import { cleanObject } from "../../utils/cleanObject";
import { formatDateToISOWithNow } from "../../utils/formatDateToISOWithNow";
import { parseBoolean } from "../../utils/parseBoolean";

const schema = Yup.object().shape({
  universoConsulta: Yup.number().required("Este campo é obrigatório!"),

  tipoPeriodo: Yup.string()
    .oneOf(["A", "D"])
    .required("Este campo é obrigatório!"),

  idGenero: Yup.array().when("radioGenero", {
    is: (val) => val && (val === "AS" || val === "NA"),
    then: (schema) =>
      schema
        .min(1, "Este campo é obrigatório!")
        .required("Este campo é obrigatório!"),
    otherwise: (schema) => schema.nullable(),
  }),

  idTipoTransferencia: Yup.object().when("universoConsulta", {
    is: (universoConsulta) => universoConsulta === 3,
    then: (schema) => schema.required("Este campo é obrigatório!").nullable(),
    otherwise: (schema) => schema.nullable(),
  }),

  idadeInicial: Yup.string().nullable(),

  idadeFinal: Yup.string()
    .nullable()
    .test(
      "idade-max-maior-ou-igual-min",
      "A Idade Inicial não pode ser menor que a Idade Final",
      function (value) {
        const { idadeInicial } = this.parent;
        if (!value || !idadeInicial) return true;
        return Number(value) >= Number(idadeInicial);
      },
    ),

  faixaPeriodo: Yup.mixed().when(
    ["tipoPeriodo", "universoConsulta", "radioRecebeVisita"],
    {
      is: (tipoPeriodo, universoConsulta, radioRecebeVisita) =>
        tipoPeriodo === "A" &&
        !!universoConsulta &&
        radioRecebeVisita?.length > 0,
      then: (schema) => schema.required("Este campo é obrigatório!"),
      otherwise: (schema) => schema.nullable(),
    },
  ),

  dataInicio: Yup.string()
    .nullable()
    .test("validDate", "Data inválida!", (value) => {
      if (!value) return true;
      if (value?.length === 10) {
        return isValid(parse(value, "dd/MM/yyyy", new Date()));
      }
      return false;
    })
    .test(
      "requiredWhenEmptyAndCondition",
      "Este campo é obrigatório!",
      function (value) {
        const { dataFim, tipoPeriodo } = this.parent;

        const precisaDeData = tipoPeriodo === "D";

        if (precisaDeData && !value && !dataFim) {
          return false;
        }
        return true;
      },
    )
    .test(
      "dataNascimentoAfterToday",
      "A data informada não pode ser uma data futura!",
      (value) => {
        if (!value) {
          return true;
        } else {
          return !isAfter(parse(value, "dd/MM/yyyy", new Date()), new Date());
        }
      },
    ),

  dataFim: Yup.string()
    .nullable()
    .test("validDate", "Data inválida!", (value) => {
      if (!value) return true;
      if (value?.length === 10) {
        return isValid(parse(value, "dd/MM/yyyy", new Date()));
      }
      return false;
    })
    .test(
      "requiredWhenEmptyAndCondition",
      "Este campo é obrigatório!",
      function (value) {
        const { dataInicio, tipoPeriodo } = this.parent;
        const precisaDeData = tipoPeriodo === "D";

        if (precisaDeData && !value && !dataInicio) {
          return false;
        }
        return true;
      },
    )
    .test(
      "dataNascimentoAfterToday",
      "A data informada não pode ser uma data futura!",
      (value) => {
        if (!value) {
          return true;
        } else {
          return !isAfter(parse(value, "dd/MM/yyyy", new Date()), new Date());
        }
      },
    )
    .test(
      "dataFimAfterInicio",
      "Data fim menor do que data início",
      function (value) {
        const { dataInicio } = this.parent;

        if (!dataInicio || !value) return true;

        const inicio = parse(dataInicio, "dd/MM/yyyy", new Date());
        const fim = parse(value, "dd/MM/yyyy", new Date());

        if (!isValid(inicio) || !isValid(fim)) return true;

        return isAfter(fim, inicio) || isEqual(fim, inicio);
      },
    ),

  idProcesso: Yup.mixed().when(
    ["tipoPeriodo", "universoConsulta", "radioPossuiProcessoJudicial"],
    {
      is: (tipoPeriodo, universoConsulta, radioPossuiProcessoJudicial) =>
        tipoPeriodo === "A" &&
        !!universoConsulta &&
        radioPossuiProcessoJudicial?.length > 0,
      then: (schema) => schema.nullable(),
      otherwise: (schema) => schema.nullable(),
    },
  ),

  idProcedimento: Yup.mixed().when(
    ["tipoPeriodo", "universoConsulta", "radioPossuiProcedimentoJudicial"],
    {
      is: (tipoPeriodo, universoConsulta, radioPossuiProcedimentoJudicial) =>
        tipoPeriodo === "A" &&
        !!universoConsulta &&
        radioPossuiProcedimentoJudicial?.length > 0,

      then: (schema) => schema.required("Este campo é obrigatório!"),
      otherwise: (schema) => schema.nullable(),
    },
  ),

  radioCriterioAtribuicaoCompetencia: Yup.string().required(
    "Este campo é obrigatório!",
  ),
});

export const FiltersRelatorioCriancaGeral = ({
  entidade,
  data,
  setData,
  setLoading,
  roleKeycloack,
  exclusao,
  atualizarDadosTabela,
  setMostrarTabela,
  mostrarTabela,
  children,
  loading,
}) => {
  const formRef = useRef(null);
  const autoSetTransferenciasRef = useRef({});
  const location = useLocation();
  const initialLoad = useRef(true);
  const auth = useAuth();
  const token = auth?.user?.access_token;
  const [innerTabIndex, setInnerTabIndex] = useState(0);
  const [activeTabIndex, setActiveTabIndex] = useState(() => {
    const tempObj = JSON.parse(sessionStorage.getItem(entidade + "-filter"));
    return tempObj?.activeTabIndex ?? 0;
  });
  const [passedTabs, setPassedTabs] = useState(() => {
    const tempObj = JSON.parse(sessionStorage.getItem(entidade + "-filter"));
    return tempObj?.passedTabs ?? [0];
  });
  const isMobile = useBreakpointValue({ base: true, md: false });

  const placement = useBreakpointValue({ md: "right" });

  const tabs = useMemo(() => {
    let visibletabs = [
      {
        label: "Universo da Consulta",
        fields: [
          "universoConsulta",
          "dataInicio",
          "dataFim",
          "idsMotivosEntrada",
          "idTipoTransferencia",
          "idJustificativas",
          "tipoPeriodo",
          "entradasPorTransferencias",
          "saidaTransferencia",
        ],
      },
      {
        label: "Localização do Serviço de Acolhimento",
        fields: [
          "idBairro",
          "idEstado",
          "idMunicipio",
          "idsServicosAcolhimento",
        ],
      },
      {
        label: "Órgãos",
        fields: [
          "idMunicipioOrgao, idPromotoriaOrgaos",
          "idVaraOrgaos",
          "radioCriterioAtribuicaoCompetencia",
        ],
      },
      {
        label: "Dados Básicos",
        fields: [
          "pesquisarFichas",
          "idGenero",
          "idRaca",
          "idadeInicial",
          "idadeFinal",
        ],
      },

      {
        label: "Referência Familiar e Comunitária",
        fields: ["idTipoReferencia", "nomeReferencia", "detalhamentoIrmaos"],
      },

      {
        label: "Saúde",
        fields: [
          "deficiencias",
          "doencas",
          "radioMedicacaoContinua",
          "radioCartaoVacinacao",
          "radioProblemaSaude",
          "radioDeficiencia",
        ],
      },
      {
        label: "Educação",
        fields: [
          "radioFrequentaEscola",
          "radioEducacaoEspecial",
          "radioEscolaridadeRepetida",
        ],
      },
      {
        label: "Profissionalização",
        fields: [
          "radioPossuiCurso",
          "radioTrabalhaAtualmente",
          "radioFazEstagio",
          "radioJovemAprendiz",
          "radioCurriculoVitae",
        ],
      },
      {
        label: "Visita",
        fields: ["radioRecebeVisita", "faixaPeriodo"],
      },

      {
        label: "Situação Jurídica",
        fields: [
          "idTipoSentenca",
          "idProcesso",
          "idProcedimento",
          "radioPossuiProcessoJudicial",
          "radioPossuiProcedimentoJudicial",
          "radioDecisaoLiminar",
          "radioSentenca",
          "radioRecurso",
          "radioTransitoJulgado",
        ],
      },
      {
        label: "Situação quanto à Adoção",
        fields: ["radioCumpreRequisito"],
      },

      {
        label: "Programa de Apadrinhamento",
        fields: ["radioIncluidoPrograma", "radioSendoApadrinhado"],
      },
    ];

    if (roleKeycloack?.includes(dashboardFunc.abasServicoConselho)) {
      const allowedLabels = [
        "Universo da Consulta",
        "Localização do Serviço de Acolhimento",
        "Órgãos",
        "Caracterização do Serviço de Acolhimento",
        "Perfil de Atendimento",
        "Vagas Disponíveis",
      ];

      return visibletabs.filter((tab) => allowedLabels.includes(tab.label));
    }

    if (roleKeycloack?.includes(dashboardFunc.abasDefensoriaOcultar)) {
      visibletabs = visibletabs.filter(
        (tab) =>
          tab.label !== "Equipe de Referência" && tab.label !== "Voluntariado",
      );
    }

    return visibletabs;
  }, [roleKeycloack]);

  const EMPTY_FILTER_VALUES = {
    universoConsulta: "",
    dataInicio: "",
    dataFim: "",
    idsMotivosEntrada: "",
    idTipoTransferencia: "",
    idJustificativas: "",
    tipoPeriodo: "",
    entradasPorTransferencias: "",
    saidaTransferencia: "",
    maioresDezoito: "",

    idBairro: [],
    idEstado: [
      [
        {
          id: 33,
          nome: "RIO DE JANEIRO",
          sigla: "RJ",
        },
      ],
    ],
    idMunicipio: [],
    idsServicosAcolhimento: "",
    idMunicipioOrgao: [],
    idPromotoriaOrgaos: [],
    idVaraOrgaos: [],
    radioCriterioAtribuicaoCompetencia: "C",

    pesquisarFichas: [],
    idadeInicial: "",
    idadeFinal: "",
    idGenero: "",
    idRaca: "",
    idTipoReferencia: "",
    nomeReferencia: "",
    detalhamentoIrmaos: [],
    deficiencias: "",
    doencas: "",
    radioMedicacaoContinua: [],
    radioCartaoVacinacao: [],
    radioProblemaSaude: [],
    radioDeficiencia: [],
    radioFrequentaEscola: [],
    radioEducacaoEspecial: [],
    radioEscolaridadeRepetida: [],
    radioPossuiCurso: [],
    radioTrabalhaAtualmente: [],
    radioFazEstagio: [],
    radioJovemAprendiz: [],
    radioCurriculoVitae: [],
    faixaPeriodo: "",
    radioRecebeVisita: [],
    idTipoSentenca: "",
    idProcesso: "",
    idProcedimento: "",
    radioPossuiProcessoJudicial: [],
    radioPossuiProcedimentoJudicial: [],
    radioDecisaoLiminar: [],
    radioSentenca: [],
    radioRecurso: [],
    radioTransitoJulgado: [],
    radioCumpreRequisito: [],
    radioIncluidoPrograma: [],
    radioSendoApadrinhado: [],
    pageSize: 10,
    page: 0,
  };

  const [initialValues, setInitialValues] = useState({
    ...EMPTY_FILTER_VALUES,
  });

  const CAMPOS_ABAS_RESTRITAS = {
    Órgãos: [
      "idMunicipioOrgao",
      "idPromotoriaOrgaos",
      "idVaraOrgaos",
      "radioCriterioAtribuicaoCompetencia",
    ],
    Saúde: [
      "deficiencias",
      "doencas",
      "radioMedicacaoContinua",
      "radioCartaoVacinacao",
      "radioProblemaSaude",
      "radioDeficiencia",
    ],
    Educação: [
      "radioFrequentaEscola",
      "radioEducacaoEspecial",
      "radioEscolaridadeRepetida",
    ],
    Profissionalização: [
      "radioPossuiCurso",
      "radioTrabalhaAtualmente",
      "radioFazEstagio",
      "radioJovemAprendiz",
      "radioCurriculoVitae",
    ],
    Visita: ["radioRecebeVisita", "faixaPeriodo"],
    "Situação Jurídica": [
      "idTipoSentenca",
      "idProcesso",
      "idProcedimento",
      "radioPossuiProcessoJudicial",
      "radioPossuiProcedimentoJudicial",
      "radioDecisaoLiminar",
      "radioSentenca",
      "radioRecurso",
      "radioTransitoJulgado",
    ],
    "Situação quanto à Adoção": ["radioCumpreRequisito"],
    "Programa de Apadrinhamento": [
      "radioIncluidoPrograma",
      "radioSendoApadrinhado",
    ],
  };

  useEffect(() => {
    setDefaultLocale("pt-br");
    registerLocale("pt-br", pt);
  }, []);

  useEffect(() => {
    const storage = sessionStorage.getItem(entidade + "-filter");

    if (storage) {
      const tempObj = JSON.parse(storage);

      const sanitizeAges = (obj) => ({
        ...obj,
        idadeMinima:
          obj?.idadeMinima === null ||
          obj?.idadeMinima === "null" ||
          obj?.idadeMinima === undefined
            ? ""
            : obj.idadeMinima,
        idadeMaxima:
          obj?.idadeMaxima === null ||
          obj?.idadeMaxima === "null" ||
          obj?.idadeMaxima === undefined
            ? ""
            : obj.idadeMaxima,
        idadeInicial:
          obj?.idadeInicial === null ||
          obj?.idadeInicial === "null" ||
          obj?.idadeInicial === undefined
            ? ""
            : obj.idadeInicial,
        idadeFinal:
          obj?.idadeFinal === null ||
          obj?.idadeFinal === "null" ||
          obj?.idadeFinal === undefined
            ? ""
            : obj.idadeFinal,
      });

      if (Array.isArray(tempObj.idGenero) && tempObj.idGenero?.length > 0) {
        tempObj.idGenero = tempObj.idGenero.map((grupo) =>
          grupo.map((genero) => ({
            ...genero,
            label: (
              <HStack justifyContent="space-between">
                <Text
                  color="#1A203D"
                  fontWeight={400}
                  fontSize={{ base: "12px", md: "14px" }}
                >
                  {genero.genero}
                </Text>
                <Tooltip label={genero.descricao} fontSize="md">
                  <span>
                    <Icon
                      aria-label="Aviso de pendente"
                      color="#6B6C71"
                      as={IoHelpCircle}
                      boxSize={{ base: "10px", md: "20px" }}
                    />
                  </span>
                </Tooltip>
              </HStack>
            ),
          })),
        );
      }

      if (Array.isArray(tempObj.idProcesso) && tempObj.idProcesso?.length > 0) {
        tempObj.idProcesso = tempObj.idProcesso.map((grupo) =>
          grupo.map((processo) => ({
            ...processo,
            label: (
              <HStack justifyContent="space-between">
                <Text
                  color="#1A203D"
                  fontWeight={400}
                  fontSize={{ base: "12px", md: "14px" }}
                >
                  {processo.descricao}
                </Text>
                <Tooltip label={processo.descricao} fontSize="md">
                  <span>
                    <Icon
                      aria-label="Aviso de pendente"
                      color="#6B6C71"
                      as={IoHelpCircle}
                      boxSize={{ base: "10px", md: "20px" }}
                    />
                  </span>
                </Tooltip>
              </HStack>
            ),
          })),
        );
      }

      if (
        Array.isArray(tempObj.idProcedimento) &&
        tempObj.idProcedimento?.length > 0
      ) {
        tempObj.idProcedimento = tempObj.idProcedimento.map((grupo) =>
          grupo.map((procedimento) => ({
            ...procedimento,
            label: (
              <HStack justifyContent="space-between" px={{ md: "10px" }}>
                <Text
                  color="#1A203D"
                  fontWeight={400}
                  fontSize={{ base: "12px", md: "14px" }}
                >
                  {procedimento.descricao}
                </Text>
                <Tooltip label={procedimento.descricao} fontSize="md">
                  <span>
                    <Icon
                      aria-label="Aviso de pendente"
                      color="#6B6C71"
                      as={IoHelpCircle}
                      boxSize={{ base: "10px", md: "20px" }}
                    />
                  </span>
                </Tooltip>
              </HStack>
            ),
          })),
        );
      }

      const sanitizedValues = sanitizeAges(tempObj);

      setInitialValues(sanitizedValues);
    }
  }, [entidade]);

  useEffect(() => {
    if (!initialLoad.current) {
      if (formRef.current) {
        formRef.current.handleSubmit();
      }
    } else {
      initialLoad.current = false;
    }
  }, [location]);

  const FocusClearField = () => {
    const { values, setFieldValue, setValues, setFieldTouched } =
      useFormikContext();

    const prevUniversoRef = useRef(values?.universoConsulta);

    useEffect(() => {
      const atual = values?.universoConsulta;
      const anterior = prevUniversoRef.current;

      if (anterior === 2 && atual !== 2 && atual !== undefined) {
        setFieldValue("tipoPeriodo", "");
        setFieldValue("dataInicio", "");
        setFieldValue("dataFim", "");

        if (atual !== 3) {
          setFieldValue("idTipoTransferencia", "");
        }
      }

      prevUniversoRef.current = atual;
    }, [values?.universoConsulta, setFieldValue]);

    useEffect(() => {
      if (values?.tipoPeriodo && values?.tipoPeriodo !== "A") {
        const newValues = { ...values };

        Object.keys(CAMPOS_ABAS_RESTRITAS).forEach((aba) => {
          CAMPOS_ABAS_RESTRITAS[aba].forEach((campo) => {
            newValues[campo] = EMPTY_FILTER_VALUES[campo];
          });
        });

        newValues.pesquisarFichas = [];
        newValues.detalhamentoIrmaos = [];

        if (
          Object.keys(CAMPOS_ABAS_RESTRITAS).includes(
            tabs[activeTabIndex]?.label,
          )
        ) {
          setActiveTabIndex(0);
        }

        setValues(newValues);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values?.tipoPeriodo, setValues]);

    useEffect(() => {
      if (values?.tipoPeriodo === "A") {
        setFieldValue("dataInicio", "");
        setFieldValue("dataFim", "");
      }
    }, [setFieldValue, values?.tipoPeriodo]);

    useEffect(() => {
      if (values?.radioDeficiencia === "N") {
        setFieldValue("deficiencias", "");
      }
    }, [setFieldValue, values?.radioDeficiencia]);

    useEffect(() => {
      if (values?.radioProblemaSaude === "N") {
        setFieldValue("doencas", "");
      }
    }, [setFieldValue, values?.radioProblemaSaude]);

    useEffect(() => {
      if (values?.idadeFinal !== "" && Number(values?.idadeFinal) <= 15) {
        const camposPro = CAMPOS_ABAS_RESTRITAS["Profissionalização"];

        camposPro.forEach((campo) => {
          const valorInicial = EMPTY_FILTER_VALUES[campo];
          if (values[campo] !== valorInicial) {
            setFieldValue(campo, valorInicial);
          }
        });

        if (tabs[activeTabIndex]?.label === "Profissionalização") {
          setActiveTabIndex(3);
          setFieldValue("activeTabIndex", 3);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values?.idadeFinal, setFieldValue]);

    useEffect(() => {
      const universo = values?.universoConsulta;

      if (universo === 1) {
        if (!autoSetTransferenciasRef.current[1]) {
          setFieldValue("entradasPorTransferencias", ["Possui"]);
          autoSetTransferenciasRef.current[1] = true;
        }
      } else if (universo === 2) {
        if (!autoSetTransferenciasRef.current[2]) {
          setFieldValue("saidaTransferencia", ["Possui"]);
          autoSetTransferenciasRef.current[2] = true;
        }
      } else {
        // Resetar o controle quando trocar de universo
        autoSetTransferenciasRef.current = {};
      }

      if (universo !== 3) {
        setFieldValue("idTipoTransferencia", "");
      }
    }, [values?.universoConsulta, setFieldValue]);

    useEffect(() => {
      const incluido = values?.radioIncluidoPrograma;

      if (incluido !== "S" && values.radioSendoApadrinhado !== "") {
        setFieldValue("radioSendoApadrinhado", "");
      }
    }, [
      values.radioIncluidoPrograma,
      values.radioSendoApadrinhado,
      setFieldValue,
    ]);

    useEffect(() => {
      const incluido = values?.radioFrequentaEscola;

      if (
        incluido === "N" &&
        (values.radioEducacaoEspecial !== "" ||
          values.radioEscolaridadeRepetida !== "")
      ) {
        setFieldValue("radioEducacaoEspecial", "");
        setFieldValue("radioEscolaridadeRepetida", "");
      }
    }, [
      values.radioFrequentaEscola,
      values.radioEducacaoEspecial,
      values.radioEscolaridadeRepetida,
      setFieldValue,
    ]);

    useEffect(() => {
      const current = values?.radioCartaoVacinacao || [];

      if (current.includes("SIM") && current?.length > 1) {
        setFieldValue("radioCartaoVacinacao", ["SIM"]);
      }
    }, [values.radioCartaoVacinacao, setFieldValue]);

    useEffect(() => {
      const isSomenteD = values?.universoConsulta;

      if (!isSomenteD) return;

      if (values.possuiVaga !== "") {
        setFieldValue("possuiVaga", "");
      }

      setPassedTabs((prev) => {
        const vagasTabIndex = tabs.findIndex(
          (tab) => tab.label === "Vagas Disponíveis",
        );
        if (vagasTabIndex === -1) return prev;

        if (!prev.includes(vagasTabIndex)) return prev;

        return prev.filter((tabIndex) => tabIndex !== vagasTabIndex);
      });
    }, [values.universoConsulta, values.possuiVaga, setFieldValue]);

    useEffect(() => {
      if (!values?.mantemPrograma?.includes("S")) {
        setFieldValue("encontraInscritoCMDCA", []);
      }
    }, [values?.mantemPrograma, setFieldValue]);

    useEffect(() => {
      if (
        values?.naturezaServico?.[0]?.length === 1 &&
        values?.naturezaServico?.[0]?.[0]?.id === "publica"
      ) {
        setFieldValue("registroCMDCA", []);
        setFieldValue("registroValidoCMDCA", []);
        setFieldValue("inscricaoCMAS", []);
        setFieldValue("inscricaoValidaCMAS", []);
      }
    }, [values?.naturezaServico, setFieldValue]);

    useEffect(() => {
      const isSomenteSAF =
        values?.regime?.[0]?.length === 1 &&
        values?.regime?.[0]?.[0]?.id === "SAF";

      if (isSomenteSAF && values?.possuiVoluntariado !== "") {
        setFieldValue("possuiVoluntariado", "");
      }
    }, [values?.regime, values?.possuiVoluntariado, setFieldValue]);

    useEffect(() => {
      if (values?.radioGenero === "SR") {
        setFieldValue("idGenero", "");
      } else if (values?.radioGenero === "AS" || values?.radioGenero === "NA") {
        setFieldTouched("idGenero", true);
      }
    }, [values?.radioGenero, setFieldValue, setFieldTouched]);

    useEffect(() => {
      if (mostrarTabela) {
        setActiveTabIndex(tabs?.length);
        setFieldValue("activeTabIndex", tabs?.length);
        if (innerTabIndex === 0) setInnerTabIndex(0);
      }
    }, [setFieldValue, values?.activeTabIndex]);

    return null;
  };

  const fieldHasValue = (value) => {
    if (Array.isArray(value)) return value?.length > 0;
    if (typeof value === "string") return value.trim() !== "";
    if (typeof value === "boolean") return true;
    if (value === 0) return true;
    return !!value;
  };

  const isTabComplete = (index, props) => {
    const tab = tabs[index];

    if (
      tab.label === "Vagas Disponíveis" &&
      props.values?.universoConsulta &&
      props?.values?.tipoPeriodo
    ) {
      return false;
    }

    const fields = tab.fields.flatMap((f) =>
      f.split(",").map((name) => name.trim()),
    );

    const hasError = fields.some((field) => props.errors?.[field]);

    const hasAnyValue = fields.some((field) =>
      fieldHasValue(props.values?.[field]),
    );

    return passedTabs.includes(index) && hasAnyValue && !hasError;
  };

  const funcPesquisarRelatorioPendencia = useCallback(
    (valuesToSend) => {
      let request = RelatorioService.getRelatorioCriancaGeral(valuesToSend);

      request
        .then((res) => {
          sessionStorage.setItem(entidade + "-filterSearch", true);
          const tempObj = JSON.parse(
            sessionStorage.getItem(entidade + "-filter"),
          );

          sessionStorage.setItem(
            entidade + "-filter",
            JSON.stringify({
              ...tempObj,
              totalItems: res.data.totalElements,
              totalPages: res.data.totalPages,
            }),
          );

          setData(res.data);
          setMostrarTabela(true);
          sessionStorage.setItem(
            "mostrarTabelaRelatorio",
            JSON.stringify(true),
          );
        })
        .catch((error) => {
          setData([]);
          Swal.fire({
            title: "Atenção!",
            text: error.response.data.message
              ? error.response.data.message
              : "",
            icon: "error",
            confirmButtonText: "Fechar",
            confirmButtonColor: "#169499",
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [entidade, setData, setLoading, setMostrarTabela],
  );

  useEffect(() => {
    const tempObj = JSON.parse(sessionStorage.getItem("table-" + entidade));
    const storage = JSON.parse(sessionStorage.getItem(entidade + "-filter"));
    const pesquisou = sessionStorage.getItem(entidade + "-filterSearch");
    const passou = sessionStorage.getItem("crianca-passou");
    const servicoPassou = sessionStorage.getItem("servico-passou");

    if (atualizarDadosTabela || (pesquisou && (passou || servicoPassou))) {
      setLoading(true);

      const idsProcesso = (storage?.idProcesso || [])
        ?.flat()
        ?.map((item) => item.id)
        ?.filter(Boolean);
      const idsTipoSentenca = (storage?.idTipoSentenca || [])
        ?.flat()
        ?.map((item) => item.id)
        ?.filter(Boolean);
      const idsProcedimento = (storage?.idProcedimento || [])
        ?.flat()
        ?.map((item) => item.id)
        ?.filter(Boolean);

      const idsDeficiencias = storage?.deficiencias
        ? (() => {
            const flat = storage?.deficiencias.flat();

            const hasTodos = flat.some((item) => item.id === "");

            if (hasTodos) return [0];

            return flat
              .map((item) => item.idTipoDeficiencia ?? item.value)
              .filter((id) => id !== null && id !== undefined && id !== "");
          })()
        : [];

      const idsDoencas = storage?.doencas
        ? (() => {
            const flat = storage?.doencas.flat();

            const hasTodos = flat.some((item) => item.id === "");

            if (hasTodos) return [0];

            return flat
              .map((item) => item.idDoenca ?? item.value)
              .filter((id) => id !== null && id !== undefined && id !== "");
          })()
        : [];

      const generos = (storage?.idGenero || [])
        ?.flat()
        ?.map((item) => item.id)
        ?.filter(Boolean);

      const racas = (storage?.idRaca || [])
        ?.flat()
        ?.map((item) => item.id)
        ?.filter(Boolean);

      const idTipoRef = storage?.idTipoReferencia
        ? (() => {
            const flat = storage?.idTipoReferencia.flat();

            const hasTodos = flat.some((item) => item.id === "");

            if (hasTodos) return [0];

            return flat
              .map((item) => item.id ?? item.value)
              .filter((id) => id !== null && id !== undefined && id !== "");
          })()
        : [];

      const nomeServicosAcolhimentos = (storage?.idsServicosAcolhimento || [])
        ?.flat()
        ?.map((item) => item.nomeFantasia)
        ?.filter(Boolean);

      const idsJustificativas = (storage?.idJustificativas || [])
        ?.flat()
        ?.map((item) => item.id)
        ?.filter(Boolean);

      const idsMotivo = (storage?.idsMotivosEntrada || [])
        ?.flat()
        ?.map((item) => item.id)
        ?.filter(Boolean);
      const idsServicos = (storage?.idsServicosAcolhimento || [])
        ?.flat()
        ?.map((item) => item.id)
        ?.filter(Boolean);
      const municipiosOrgaos = (storage?.idMunicipioOrgao || [])
        ?.flat()
        ?.map((item) => item.id)
        ?.filter((id) => id !== "" && id != null);
      const promotoriasOrgaos = (storage?.idPromotoriaOrgaos || [])
        ?.flat()
        ?.map((item) => ({
          id: item.id,
          idMunicipio: item.idMunicipio,
        }))
        ?.filter((id) => id !== "" && id != null);
      const varasOrgaos = (storage?.idVaraOrgaos || [])
        ?.flat()
        ?.map((item) => ({
          id: item.id,
          idMunicipio: item.idMunicipio,
        }))
        ?.filter((id) => id !== "" && id != null);

      const idadeMin = [storage?.idadeInicial].find(
        (v) => v != null && v !== "" && v !== "null",
      );

      const idadeMax = [storage?.idadeFinal].find(
        (v) => v != null && v !== "" && v !== "null",
      );

      const valuesToSend = {
        universoConsulta: {
          universo:
            storage?.universoConsulta === 1
              ? "ACOLHIDO"
              : storage?.universoConsulta === 2
                ? "DESLIGADO"
                : storage?.universoConsulta === 3
                  ? "TRANSFERENCIA"
                  : "PENDENTE",
          idsServicosAcolhimento: idsServicos,
          idsMotivosEntrada: idsMotivo,
          tipoPeriodo:
            storage?.tipoPeriodo === "A"
              ? "SITUACAO_ATUAL"
              : storage?.tipoPeriodo === "D"
                ? "DATA"
                : "",

          entradasPorTransferencias:
            storage?.entradasPorTransferencias?.includes("Possui")
              ? true
              : false,
          saidaTransferencia: storage?.saidaTransferencia?.includes("Possui")
            ? true
            : false,
          maioresDezoito: storage?.maioresDezoito?.includes("Possui")
            ? true
            : false,
          dataInicio: storage?.dataInicio
            ? formatDateToISOWithNow(storage?.dataInicio)
            : "",
          dataFim: storage?.dataFim
            ? formatDateToISOWithNow(storage?.dataFim)
            : "",
          idJustificativas: idsJustificativas,
          idTipoTransferencia: storage?.idTipoTransferencia?.id
            ? storage?.idTipoTransferencia?.id
            : null,
        },

        localizacaoServicoAcolhimento: {
          estados: storage?.idEstado
            ? storage?.idEstado
                .flat()
                .map((item) => item.id)
                .filter((item) => item.id !== "")
            : [],
          municipios: storage?.idMunicipio
            ? storage?.idMunicipio
                .flat()
                .map((item) => ({
                  id: item.id,
                  idEstado: item.idEstado,
                }))
                .filter((item) => item.id !== "")
            : [],
          bairros: storage?.idBairro
            ? storage?.idBairro
                .flat()
                .map((item) => ({
                  id: item.id,
                  idMunicipio: item.idMunicipio,
                }))
                .filter((item) => item.id !== "")
            : [],
          nomeServicosAcolhimentos: nomeServicosAcolhimentos,
        },

        orgaos: cleanObject({
          municipios: municipiosOrgaos,
          promotorias: promotoriasOrgaos,
          varasInfancias: varasOrgaos,
          orgaoConstaFichaCriancaAdoslecente:
            storage?.radioCriterioAtribuicaoCompetencia === "C" ? true : "",
          orgaoConstaFichaServicoAcolhimento:
            storage?.radioCriterioAtribuicaoCompetencia === "S" ? true : "",
          mesmoOrgaoFichaCriancaAdoslecenteEFichaServicoAcolhimento:
            storage?.radioCriterioAtribuicaoCompetencia === "CS" ? true : "",
        }),

        dadosBasicos: cleanObject({
          generos: generos,
          racasCores: racas,
          faixaEtariaDe: idadeMin,
          faixaEtariaAte: idadeMax,
          comNomeSocial: storage?.pesquisarFichas?.includes("S") ? true : "",
          comNomeFicticio: storage?.pesquisarFichas?.includes("F") ? true : "",
          comDataNascimentoEstimada: storage?.pesquisarFichas?.includes("N")
            ? true
            : "",
        }),
        referenciaFamiliarComunitaria: cleanObject({
          tiposReferencia: idTipoRef,
          nomeReferencia: storage?.nomeReferencia,
          irmaosMesmoServico: storage?.detalhamentoIrmaos?.includes("A")
            ? true
            : "",
          irmaosServicosDiferentes: storage?.detalhamentoIrmaos?.includes("AD")
            ? true
            : "",
          irmaosSemHistoricoAcolhimento: storage?.detalhamentoIrmaos?.includes(
            "H",
          )
            ? true
            : "",
          irmaosDesligados: storage?.detalhamentoIrmaos?.includes("D")
            ? true
            : "",
        }),
        saude: cleanObject({
          possuiDeficiencia: parseBoolean(storage?.radioDeficiencia),
          tiposDeficiencia: idsDeficiencias,
          possuiProblemaSaude: parseBoolean(storage?.radioProblemaSaude),
          tiposDoenca: idsDoencas,
          cartaoVacinacao:
            storage?.radioCartaoVacinacao?.length > 0
              ? storage?.radioCartaoVacinacao
              : [],
          usaMedicacaoContinua: parseBoolean(storage?.radioMedicacaoContinua),
        }),
        educacao: cleanObject({
          frequentaEscola: parseBoolean(storage?.radioFrequentaEscola),
          educacaoEspecial: parseBoolean(storage?.radioEducacaoEspecial),
          escolaridadeRepetida: parseBoolean(
            storage?.radioEscolaridadeRepetida,
          ),
        }),
        profissionalizacao: cleanObject({
          possuiCursoProfissionalizante: parseBoolean(
            storage?.radioPossuiCurso,
          ),
          trabalhaAtualmente: parseBoolean(storage?.radioTrabalhaAtualmente),
          fazEstagio: parseBoolean(storage?.radioFazEstagio),
          matriculadoJovemAprendiz: parseBoolean(storage?.radioJovemAprendiz),
          possuiCurriculoVitae: parseBoolean(storage?.radioCurriculoVitae),
        }),

        ...(storage?.radioRecebeVisita &&
          typeof storage?.radioRecebeVisita === "string" && {
            visita: cleanObject({
              recebeVisita: parseBoolean(storage?.radioRecebeVisita),
              faixaPeriodo:
                storage?.faixaPeriodo?.id === 1
                  ? "NO_MES"
                  : storage?.faixaPeriodo?.id === 2
                    ? "ULTIMOS_3_MESES"
                    : storage?.faixaPeriodo?.id === 3
                      ? "ULTIMOS_6_MESES"
                      : storage?.faixaPeriodo?.id === 4
                        ? "EM_QUALQUER_PERIODO"
                        : "EM_QUALQUER_PERIODO",
            }),
          }),

        situacaoJuridica: cleanObject({
          possuiProcessoJudicial: parseBoolean(
            storage?.radioPossuiProcessoJudicial,
          ),
          tiposProcesso: idsProcesso,
          possuiProcedimentoJudicial: parseBoolean(
            storage?.radioPossuiProcedimentoJudicial,
          ),
          tiposProcedimento: idsProcedimento,
          haDecisaoLiminarFamiliaSubstituta: parseBoolean(
            storage?.radioDecisaoLiminar,
          ),
          possuiSentenca: parseBoolean(storage?.radioSentenca),
          tiposSentenca: idsTipoSentenca,
          recurso:
            storage?.radioRecurso === "S"
              ? "S"
              : storage?.radioRecurso === "N"
                ? "N"
                : storage?.radioRecurso === "I"
                  ? "I"
                  : "",
          transitoEmJulgado: parseBoolean(storage?.radioTransitoJulgado),
        }),
        situacaoAdocao: {
          cumpreRequisitosAdocao: parseBoolean(storage?.radioCumpreRequisito),
        },

        ...(storage?.radioIncluidoPrograma &&
          typeof storage?.radioIncluidoPrograma === "string" && {
            programaApadrinhamento: cleanObject({
              incluidoProgramaApadrinhamento:
                storage?.radioIncluidoPrograma === "S"
                  ? "SIM"
                  : storage?.radioIncluidoPrograma === "N"
                    ? "NAO"
                    : storage?.radioIncluidoPrograma === "SI"
                      ? "SEM_INFORMACAO"
                      : "",
              sendoApadrinhado:
                storage?.radioSendoApadrinhado === "S"
                  ? "SIM"
                  : storage?.radioSendoApadrinhado === "N"
                    ? "NAO"
                    : storage?.radioSendoApadrinhado === "SI"
                      ? "SEM_INFORMACAO"
                      : "",
            }),
          }),
        pageSize: storage?.pageSize ? storage.pageSize : "",
        page: storage?.page ? storage.page : 0,
        sortField: tempObj ? tempObj[0]?.id : "nomeCrianca",
        sortType: tempObj ? (tempObj[0]?.desc ? "DESC" : "ASC") : "ASC",
      };

      funcPesquisarRelatorioPendencia(valuesToSend);
    }
  }, [
    exclusao,
    atualizarDadosTabela,
    location.state,
    entidade,
    funcPesquisarRelatorioPendencia,
    setLoading,
    roleKeycloack,
  ]);

  const clearAllFieldsWithSetFieldValue = (props) => {
    props.resetForm({
      values: {
        ...EMPTY_FILTER_VALUES,
        activeTabIndex: 0,
      },
    });

    setTimeout(() => {
      props.validateForm();
    }, 0);
  };

  function ClearFiltersButton({ tabs, tabLabel, props }) {
    const handleClear = () => {
      const selectedTab = tabs.find((tab) => tab.label === tabLabel);

      if (!selectedTab) return;

      selectedTab.fields.forEach((fieldName) => {
        const cleanNames = fieldName.split(",").map((f) => f.trim());

        cleanNames.forEach((f) => {
          props.setFieldValue(f, "");
        });
      });

      setTimeout(() => {
        props.validateForm();
      }, 0);
    };

    return (
      <Button
        type="button"
        px={4}
        h={"28px"}
        color="#169499"
        border="1px solid #169499"
        backgroundColor="#EBF3F2"
        fontWeight={1000}
        fontSize="14px"
        isDisabled={props.isSubmitting || loading}
        borderRadius={"99px"}
        _hover={{ bg: "#DDEBEA" }}
        onClick={handleClear}
      >
        Limpar Filtros
      </Button>
    );
  }

  const handleSubmit = (values, actions, helpers) => {
    const storage = JSON.parse(sessionStorage.getItem(entidade + "-filter"));
    setTimeout(() => {
      const {
        universoConsulta,
        dataInicio,
        dataFim,
        idsMotivosEntrada,
        idTipoTransferencia,
        idJustificativas,
        tipoPeriodo,
        entradasPorTransferencias,
        saidaTransferencia,
        maioresDezoito,

        idBairro,
        idEstado,
        idMunicipio,
        idsServicosAcolhimento,
        idMunicipioOrgao,
        idPromotoriaOrgaos,
        idVaraOrgaos,
        radioCriterioAtribuicaoCompetencia,
        pesquisarFichas,
        idadeInicial,
        idadeFinal,
        idGenero,
        idRaca,
        idTipoReferencia,
        nomeReferencia,
        detalhamentoIrmaos,
        deficiencias,
        doencas,
        radioMedicacaoContinua,
        radioCartaoVacinacao,
        radioProblemaSaude,
        radioDeficiencia,
        radioFrequentaEscola,
        radioEducacaoEspecial,
        radioEscolaridadeRepetida,
        radioPossuiCurso,
        radioTrabalhaAtualmente,
        radioFazEstagio,
        radioJovemAprendiz,
        radioCurriculoVitae,
        faixaPeriodo,
        radioRecebeVisita,
        idTipoSentenca,
        idProcesso,
        idProcedimento,
        radioPossuiProcessoJudicial,
        radioPossuiProcedimentoJudicial,
        radioDecisaoLiminar,
        radioSentenca,
        radioRecurso,
        radioTransitoJulgado,
        radioCumpreRequisito,
        radioIncluidoPrograma,
        radioSendoApadrinhado,
      } = values;

      values.page = 0;
      values.pageSize = storage?.pageSize ? storage.pageSize : 10;

      const idsProcesso = (idProcesso || [])
        ?.flat()
        ?.map((item) => item.id)
        ?.filter(Boolean);
      const idsTipoSentenca = (idTipoSentenca || [])
        ?.flat()
        ?.map((item) => item.id)
        ?.filter(Boolean);
      const idsProcedimento = (idProcedimento || [])
        ?.flat()
        ?.map((item) => item.id)
        ?.filter(Boolean);

      const idsDeficiencias = deficiencias
        ? (() => {
            const flat = deficiencias.flat();

            const hasTodos = flat.some((item) => item.id === "");

            if (hasTodos) return [0];

            return flat
              .map((item) => item.idTipoDeficiencia ?? item.value)
              .filter((id) => id !== null && id !== undefined && id !== "");
          })()
        : [];

      const idsDoencas = doencas
        ? (() => {
            const flat = doencas.flat();

            const hasTodos = flat.some((item) => item.id === "");

            if (hasTodos) return [0];

            return flat
              .map((item) => item.idDoenca ?? item.value)
              .filter((id) => id !== null && id !== undefined && id !== "");
          })()
        : [];

      const generos = (idGenero || [])
        ?.flat()
        ?.map((item) => item.id)
        ?.filter(Boolean);

      const racas = (idRaca || [])
        ?.flat()
        ?.map((item) => item.id)
        ?.filter(Boolean);

      const idTipoRef = idTipoReferencia
        ? (() => {
            const flat = idTipoReferencia.flat();

            const hasTodos = flat.some((item) => item.id === "");

            if (hasTodos) return [0];

            return flat
              .map((item) => item.id ?? item.value)
              .filter((id) => id !== null && id !== undefined && id !== "");
          })()
        : [];

      const nomeServicosAcolhimentos = (idsServicosAcolhimento || [])
        ?.flat()
        ?.map((item) => item.nomeFantasia)
        ?.filter(Boolean);

      const idsJustificativas = (idJustificativas || [])
        ?.flat()
        ?.map((item) => item.id)
        ?.filter(Boolean);

      const idsMotivo = (idsMotivosEntrada || [])
        ?.flat()
        ?.map((item) => item.id)
        ?.filter(Boolean);
      const idsServicos = (idsServicosAcolhimento || [])
        ?.flat()
        ?.map((item) => item.id)
        ?.filter(Boolean);
      const municipiosOrgaos = (idMunicipioOrgao || [])
        ?.flat()
        ?.map((item) => item.id)
        ?.filter((id) => id !== "" && id != null);
      const promotoriasOrgaos = (idPromotoriaOrgaos || [])
        ?.flat()
        ?.map((item) => ({
          id: item.id,
          idMunicipio: item.idMunicipio,
        }))
        ?.filter((id) => id !== "" && id != null);
      const varasOrgaos = (idVaraOrgaos || [])
        ?.flat()
        ?.map((item) => ({
          id: item.id,
          idMunicipio: item.idMunicipio,
        }))
        ?.filter((id) => id !== "" && id != null);

      const idadeMin = [idadeInicial].find(
        (v) => v != null && v !== "" && v !== "null",
      );

      const idadeMax = [idadeFinal].find(
        (v) => v != null && v !== "" && v !== "null",
      );
      const valuesToSend = {
        universoConsulta: {
          universo:
            universoConsulta === 1
              ? "ACOLHIDO"
              : universoConsulta === 2
                ? "DESLIGADO"
                : universoConsulta === 3
                  ? "TRANSFERENCIA"
                  : "PENDENTE",
          idsServicosAcolhimento: idsServicos,
          idsMotivosEntrada: idsMotivo,
          tipoPeriodo:
            tipoPeriodo === "A"
              ? "SITUACAO_ATUAL"
              : tipoPeriodo === "D"
                ? "DATA"
                : "",

          entradasPorTransferencias: entradasPorTransferencias?.includes(
            "Possui",
          )
            ? true
            : false,
          saidaTransferencia: saidaTransferencia?.includes("Possui")
            ? true
            : false,
          maioresDezoito: maioresDezoito?.includes("Possui") ? true : false,
          dataInicio: dataInicio ? formatDateToISOWithNow(dataInicio) : "",
          dataFim: dataFim ? formatDateToISOWithNow(dataFim) : "",
          idJustificativas: idsJustificativas,
          idTipoTransferencia: idTipoTransferencia?.id
            ? idTipoTransferencia?.id
            : null,
        },

        localizacaoServicoAcolhimento: {
          estados: idEstado
            ? idEstado
                .flat()
                .map((item) => item.id)
                .filter((item) => item.id !== "")
            : [],
          municipios: idMunicipio
            ? idMunicipio
                .flat()
                .map((item) => ({
                  id: item.id,
                  idEstado: item.idEstado,
                }))
                .filter((item) => item.id !== "")
            : [],
          bairros: idBairro
            ? idBairro
                .flat()
                .map((item) => ({
                  id: item.id,
                  idMunicipio: item.idMunicipio,
                }))
                .filter((item) => item.id !== "")
            : [],
          nomeServicosAcolhimentos: nomeServicosAcolhimentos,
        },

        orgaos: cleanObject({
          municipios: municipiosOrgaos,
          promotorias: promotoriasOrgaos,
          varasInfancias: varasOrgaos,
          orgaoConstaFichaCriancaAdoslecente:
            radioCriterioAtribuicaoCompetencia === "C" ? true : "",
          orgaoConstaFichaServicoAcolhimento:
            radioCriterioAtribuicaoCompetencia === "S" ? true : "",
          mesmoOrgaoFichaCriancaAdoslecenteEFichaServicoAcolhimento:
            radioCriterioAtribuicaoCompetencia === "CS" ? true : "",
        }),

        dadosBasicos: cleanObject({
          generos: generos,
          racasCores: racas,
          faixaEtariaDe: idadeMin,
          faixaEtariaAte: idadeMax,
          comNomeSocial: pesquisarFichas?.includes("S") ? true : "",
          comNomeFicticio: pesquisarFichas?.includes("F") ? true : "",
          comDataNascimentoEstimada: pesquisarFichas?.includes("N") ? true : "",
        }),
        referenciaFamiliarComunitaria: cleanObject({
          tiposReferencia: idTipoRef,
          nomeReferencia: nomeReferencia,
          irmaosMesmoServico: detalhamentoIrmaos?.includes("A") ? true : "",
          irmaosServicosDiferentes: detalhamentoIrmaos?.includes("AD")
            ? true
            : "",
          irmaosSemHistoricoAcolhimento: detalhamentoIrmaos?.includes("H")
            ? true
            : "",
          irmaosDesligados: detalhamentoIrmaos?.includes("D") ? true : "",
        }),
        saude: cleanObject({
          possuiDeficiencia: parseBoolean(radioDeficiencia),
          tiposDeficiencia: idsDeficiencias,
          possuiProblemaSaude: parseBoolean(radioProblemaSaude),
          tiposDoenca: idsDoencas,
          cartaoVacinacao:
            radioCartaoVacinacao?.length > 0 ? radioCartaoVacinacao : "",
          usaMedicacaoContinua: parseBoolean(radioMedicacaoContinua),
        }),
        educacao: cleanObject({
          frequentaEscola: parseBoolean(radioFrequentaEscola),
          educacaoEspecial: parseBoolean(radioEducacaoEspecial),
          escolaridadeRepetida: parseBoolean(radioEscolaridadeRepetida),
        }),
        profissionalizacao: cleanObject({
          possuiCursoProfissionalizante: parseBoolean(radioPossuiCurso),
          trabalhaAtualmente: parseBoolean(radioTrabalhaAtualmente),
          fazEstagio: parseBoolean(radioFazEstagio),
          matriculadoJovemAprendiz: parseBoolean(radioJovemAprendiz),
          possuiCurriculoVitae: parseBoolean(radioCurriculoVitae),
        }),
        ...(radioRecebeVisita &&
          typeof radioRecebeVisita === "string" && {
            visita: cleanObject({
              recebeVisita: parseBoolean(radioRecebeVisita),
              faixaPeriodo:
                faixaPeriodo?.id === 1
                  ? "NO_MES"
                  : faixaPeriodo?.id === 2
                    ? "ULTIMOS_3_MESES"
                    : faixaPeriodo?.id === 3
                      ? "ULTIMOS_6_MESES"
                      : "EM_QUALQUER_PERIODO",
            }),
          }),
        situacaoJuridica: cleanObject({
          possuiProcessoJudicial: parseBoolean(radioPossuiProcessoJudicial),
          tiposProcesso: idsProcesso,
          possuiProcedimentoJudicial: parseBoolean(
            radioPossuiProcedimentoJudicial,
          ),
          tiposProcedimento: idsProcedimento,
          haDecisaoLiminarFamiliaSubstituta: parseBoolean(radioDecisaoLiminar),
          possuiSentenca: parseBoolean(radioSentenca),
          tiposSentenca: idsTipoSentenca,
          recurso:
            radioRecurso === "S"
              ? "S"
              : radioRecurso === "N"
                ? "N"
                : radioRecurso === "I"
                  ? "I"
                  : "",
          transitoEmJulgado: parseBoolean(radioTransitoJulgado),
        }),
        situacaoAdocao: cleanObject({
          cumpreRequisitosAdocao: parseBoolean(radioCumpreRequisito),
        }),

        ...(radioIncluidoPrograma &&
          typeof radioIncluidoPrograma === "string" && {
            programaApadrinhamento: cleanObject({
              incluidoProgramaApadrinhamento:
                radioIncluidoPrograma === "S"
                  ? "SIM"
                  : radioIncluidoPrograma === "N"
                    ? "NAO"
                    : radioIncluidoPrograma === "SI"
                      ? "SEM_INFORMACAO"
                      : "",
              sendoApadrinhado:
                radioSendoApadrinhado === "S"
                  ? "SIM"
                  : radioSendoApadrinhado === "N"
                    ? "NAO"
                    : radioSendoApadrinhado === "SI"
                      ? "SEM_INFORMACAO"
                      : "",
            }),
          }),

        pageSize: storage?.pageSize ? storage.pageSize : 10,
        page: 0,
      };

      if (entidade === "relatorio-crianca-adolescente-geral") {
        values.desc = false;
        values.sort = "nomeCrianca";
        valuesToSend.sortField = "nomeCrianca";
        valuesToSend.sortType = "ASC";
        sessionStorage.setItem(
          "table-relatorio-crianca-adolescente-geral",
          JSON.stringify([{ id: "nomeCrianca", desc: false }]),
        );
      }

      setLoading(true);
      sessionStorage.setItem(
        entidade + "-filter",
        JSON.stringify({ ...values, passedTabs }),
      );

      if (entidade === "relatorio-crianca-adolescente-geral") {
        funcPesquisarRelatorioPendencia(valuesToSend);
      }

      actions.setSubmitting(false);
    }, 200);
  };

  return roleKeycloack &&
    roleKeycloack?.includes(dashboardFunc.relatorioCriancaAdolescenteGeral) ? (
    <Flex flexDir="column" alignItems="center" w="100%">
      <Formik
        validationSchema={schema}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        innerRef={formRef}
        enableReinitialize
        validateOnMount
      >
        {(props) => (
          <Form style={{ width: "100%" }}>
            <FocusClearField />

            <Tabs
              orientation="vertical"
              variant="unstyled"
              width="100%"
              index={activeTabIndex}
            >
              <Box
                display="flex"
                width="100%"
                flexDirection={{ base: "column", lg: "row" }}
              >
                {innerTabIndex !== 1 && (
                  <Box
                    display="flex"
                    flexDirection="column"
                    borderRight={{ base: "none", lg: "1px solid #E2E8F0" }}
                    borderBottom={{ base: "1px solid #E2E8F0", lg: "none" }}
                    mb={{ base: "10px", lg: 0 }}
                    height="auto"
                    flexShrink={0}
                  >
                    <TabList
                      display="flex"
                      flexDirection="column"
                      pb={6}
                      mb={3}
                      borderBottom="1px solid #E2E8F0"
                      gap="2"
                    >
                      {tabs.map((tab, index) => {
                        const idadeAte = props.values.idadeFinal;
                        const abasRestritas = [
                          "Órgãos",
                          "Saúde",
                          "Educação",
                          "Profissionalização",
                          "Visita",
                          "Situação Jurídica",
                          "Situação quanto à Adoção",
                          "Programa de Apadrinhamento",
                        ];
                        const active = props.values.activeTabIndex ?? 0;
                        const isUniversoInvalido =
                          !props?.values.universoConsulta ||
                          !props?.values.tipoPeriodo;

                        const isDataFaltanteParaTipoD =
                          props?.values.tipoPeriodo === "D" &&
                          !props?.values.dataInicio &&
                          !props?.values.dataFim;

                        const isTransferenciaInvalida =
                          props?.values.universoConsulta === 3 &&
                          !props?.values.idTipoTransferencia;

                        const isPortaEntradaBloqueada =
                          index !== 0 &&
                          (isUniversoInvalido ||
                            isDataFaltanteParaTipoD ||
                            isTransferenciaInvalida);

                        const isBloqueadoPorIdade =
                          tab.label === "Profissionalização" &&
                          idadeAte !== "" &&
                          Number(idadeAte) <= 15;

                        const isDisabledByPeriodo =
                          props?.values.tipoPeriodo &&
                          props?.values.tipoPeriodo !== "A" &&
                          abasRestritas.includes(tab.label);

                        const isSomenteSAF =
                          props?.values?.regime?.[0]?.length === 1 &&
                          props?.values?.regime?.[0]?.[0]?.id === "SAF";

                        const isVoluntariadoBloqueado =
                          tab?.label === "Voluntariado" && isSomenteSAF;

                        const isTabLocked =
                          isPortaEntradaBloqueada ||
                          (tab?.label === "Vagas Disponíveis" &&
                            props?.values?.universoConsulta &&
                            props?.values?.tipoPeriodo) ||
                          isVoluntariadoBloqueado ||
                          isDisabledByPeriodo ||
                          isBloqueadoPorIdade;

                        return (
                          <Tooltip
                            key={index}
                            isDisabled={
                              !isDisabledByPeriodo && !isBloqueadoPorIdade
                            }
                            label={
                              isBloqueadoPorIdade
                                ? ""
                                : "Habilitado somente com o filtro “Situação Atual” ativado em Universo da Consulta."
                            }
                            fontSize="md"
                            maxW="400px"
                            placement={placement}
                            hasArrow
                          >
                            <Tab
                              key={index}
                              pointerEvents="all"
                              justifyContent="space-between"
                              alignItems="center"
                              border={`1px solid ${
                                passedTabs.includes(index) &&
                                isTabComplete(index, props) &&
                                index !== active &&
                                !isTabLocked
                                  ? "#79ADD8"
                                  : "#CBD5E0"
                              }`}
                              color={
                                index === active
                                  ? "#3C8ACC"
                                  : isTabComplete(index, props) &&
                                      passedTabs.includes(index) &&
                                      !isTabLocked
                                    ? "#169499"
                                    : "#A3A7A7"
                              }
                              mx={4}
                              my={1}
                              bgColor={
                                isTabComplete(index, props) &&
                                index !== active &&
                                !isTabLocked
                                  ? "#DDEBEA"
                                  : "#F0F0F0"
                              }
                              borderRadius="99px"
                              py="10px"
                              px="14px"
                              fontWeight="600"
                              // Suas regras de opacidade e cursor originais baseadas no bloqueio
                              opacity={!isTabLocked ? 1 : 0.5}
                              cursor={!isTabLocked ? "pointer" : "not-allowed"}
                              _selected={
                                isTabLocked
                                  ? {}
                                  : {
                                      bg: "blue.50",
                                      borderColor: "blue.400",
                                    }
                              }
                              onClick={() => {
                                // Se estiver bloqueada por qualquer regra, não faz nada
                                if (isTabLocked) return;

                                setActiveTabIndex(index);
                                props.setFieldValue("activeTabIndex", index);
                                setMostrarTabela(false);

                                const current =
                                  JSON.parse(
                                    sessionStorage.getItem(
                                      entidade + "-filter",
                                    ),
                                  ) || {};

                                sessionStorage.setItem(
                                  entidade + "-filter",
                                  JSON.stringify({
                                    ...current,
                                    activeTabIndex: index,
                                    passedTabs: passedTabs.includes(index)
                                      ? passedTabs
                                      : [...passedTabs, index],
                                  }),
                                );

                                if (!passedTabs.includes(index)) {
                                  setPassedTabs((prev) => [...prev, index]);
                                }
                              }}
                            >
                              <Box display="flex" alignItems="center" gap="2">
                                {index === active ? (
                                  <FaRegEdit size={24} />
                                ) : (
                                  <HiOutlineViewGridAdd size={24} />
                                )}
                                {tab.label}
                              </Box>

                              {tabs[index].fields.some(
                                (field) => props.errors?.[field],
                              ) && (
                                <Box
                                  fontSize="12px"
                                  lineHeight={"100%"}
                                  fontWeight={600}
                                  color="#992525"
                                  px="8px"
                                  py="4px"
                                  borderRadius="100px"
                                  bgColor={"#FFD8DB"}
                                >
                                  Obrigatório
                                </Box>
                              )}

                              {(() => {
                                if (
                                  isTabComplete(index, props) &&
                                  index !== active &&
                                  !isTabLocked
                                ) {
                                  return (
                                    <GoCheckCircleFill
                                      size={24}
                                      color="green"
                                    />
                                  );
                                }

                                if (
                                  index === active &&
                                  !(
                                    tabs[index].fields.some(
                                      (field) => props.errors?.[field],
                                    ) ||
                                    (tab.label === "Status" &&
                                      !isTabComplete(index, props))
                                  )
                                ) {
                                  return <IoIosHourglass size={24} />;
                                }

                                if (index !== 0) {
                                  return isTabLocked ? (
                                    <IoMdLock size={24} />
                                  ) : (
                                    <IoMdUnlock size={24} />
                                  );
                                }
                              })()}
                            </Tab>
                          </Tooltip>
                        );
                      })}
                    </TabList>

                    <Box p="4" display="flex" flexDirection="column" gap="2">
                      <Button
                        type="submit"
                        translate="no"
                        color="#FFF"
                        backgroundColor="#169499"
                        fontWeight={700}
                        fontSize="15px"
                        lineHeight="21.6px"
                        borderRadius="99px"
                        isDisabled={mostrarTabela}
                        _hover={{ bg: "#169499" }}
                        isLoading={props.isSubmitting || loading}
                        w={"100%"}
                      >
                        Gerar Relatório
                      </Button>

                      <Button
                        type="button"
                        px={2}
                        py={5}
                        color="#169499"
                        border="1px solid #169499"
                        backgroundColor="#EBF3F2"
                        fontWeight={1000}
                        fontSize="14px"
                        lineHeight="21.6px"
                        borderRadius="99px"
                        isDisabled={props.isSubmitting || loading}
                        _hover={{ bg: "#DDEBEA" }}
                        onClick={() => {
                          sessionStorage.removeItem(entidade + "-filter");
                          sessionStorage.removeItem("servico-passou");

                          setPassedTabs([0]);
                          setActiveTabIndex(0);
                          setInnerTabIndex(0);

                          clearAllFieldsWithSetFieldValue(props);
                          props.setFieldValue("activeTabIndex", 0);

                          setMostrarTabela(false);
                        }}
                      >
                        Limpar todos os filtros
                      </Button>
                    </Box>
                  </Box>
                )}
                <Flex w="full" minW={0}>
                  <TabPanels>
                    <TabPanel px={10} py={0}>
                      <UniversoConsultaAba
                        props={props}
                        isMobile={isMobile}
                        roleKeycloack={roleKeycloack}
                      >
                        <ClearFiltersButton
                          tabs={tabs}
                          tabLabel={"Universo da Consulta"}
                          props={props}
                        />
                      </UniversoConsultaAba>
                    </TabPanel>
                    <TabPanel px={10} py={0}>
                      <LocalizacaoServicoAba props={props}>
                        <ClearFiltersButton
                          tabs={tabs}
                          tabLabel={"Localização do Serviço de Acolhimento"}
                          props={props}
                        />
                      </LocalizacaoServicoAba>
                    </TabPanel>
                    <TabPanel px={10} py={0}>
                      <OrgaosAba
                        props={props}
                        isMobile={isMobile}
                        placement={placement}
                        token={token}
                        geral={true}
                      >
                        <ClearFiltersButton
                          tabs={tabs}
                          tabLabel={"Órgãos"}
                          props={props}
                        />
                      </OrgaosAba>
                    </TabPanel>
                    <TabPanel px={10} py={0}>
                      <DadosBasicosAba props={props} isMobile={isMobile}>
                        <ClearFiltersButton
                          tabs={tabs}
                          tabLabel={"Dados Básicos"}
                          props={props}
                        />
                      </DadosBasicosAba>
                    </TabPanel>
                    <TabPanel px={10} py={0}>
                      <ReferenciaFamiliarAba props={props} isMobile={isMobile}>
                        <ClearFiltersButton
                          tabs={tabs}
                          tabLabel={"Referência Familiar e Comunitária"}
                          props={props}
                        />
                      </ReferenciaFamiliarAba>
                    </TabPanel>
                    <TabPanel px={10} py={0}>
                      <SaudeAba props={props} isMobile={isMobile}>
                        <ClearFiltersButton
                          tabs={tabs}
                          tabLabel={"Saúde"}
                          props={props}
                        />
                      </SaudeAba>
                    </TabPanel>
                    <TabPanel px={10} py={0}>
                      <EducacaoAba props={props} isMobile={isMobile}>
                        <ClearFiltersButton
                          tabs={tabs}
                          tabLabel={"Educação"}
                          props={props}
                        />
                      </EducacaoAba>
                    </TabPanel>
                    <TabPanel px={10} py={0}>
                      <ProfissionalizacaoAba
                        props={props}
                        isMobile={isMobile}
                        placement={placement}
                      >
                        <ClearFiltersButton
                          tabs={tabs}
                          tabLabel={"Profissionalização"}
                          props={props}
                        />
                      </ProfissionalizacaoAba>
                    </TabPanel>
                    <TabPanel px={10} py={0}>
                      <VisitaAba props={props}>
                        <ClearFiltersButton
                          tabs={tabs}
                          tabLabel={"Visita"}
                          props={props}
                        />
                      </VisitaAba>
                    </TabPanel>
                    <TabPanel px={10} py={0}>
                      <SituacaoJuridicaAba
                        props={props}
                        isMobile={isMobile}
                        placement={placement}
                      >
                        <ClearFiltersButton
                          tabs={tabs}
                          tabLabel={"Situação Jurídica"}
                          props={props}
                        />
                      </SituacaoJuridicaAba>
                    </TabPanel>
                    <TabPanel px={10} py={0}>
                      <SituacaoAdocaoAba props={props}>
                        <ClearFiltersButton
                          tabs={tabs}
                          tabLabel={"Situação quanto à Adoção"}
                          props={props}
                        />
                      </SituacaoAdocaoAba>
                    </TabPanel>
                    <TabPanel px={10} py={0}>
                      <ProgramaApadrinhementoAba props={props}>
                        <ClearFiltersButton
                          tabs={tabs}
                          tabLabel={"Programa de Apadrinhamento"}
                          props={props}
                        />
                      </ProgramaApadrinhementoAba>
                    </TabPanel>

                    <TabPanel mt={innerTabIndex === 1 && 6} py={0}>
                      <Tabs
                        variant="enclosed"
                        isFitted
                        index={innerTabIndex}
                        onChange={(i) => setInnerTabIndex(i)}
                      >
                        <TabList
                          display="flex"
                          alignItems="center"
                          gap={4}
                          flexWrap={{ base: "wrap", xl: "nowrap" }}
                        >
                          <Flex w="full">
                            <Tab
                              maxW="219px"
                              color="#575553"
                              _selected={{
                                border: "1px solid #DDEBEA",
                                borderBottom: "4px solid #169499",
                                color: "#169499",
                                fontWeight: 700,
                                bgColor: "#16949908",
                              }}
                            >
                              <Flex flexDirection="column" alignItems="center">
                                <Text fontSize="16px">Relatório Gerado</Text>
                                <Text
                                  color="#6A6A6AD9"
                                  fontWeight={300}
                                  fontSize="13px"
                                >{`Data: ${format(
                                  new Date(),
                                  "dd/MM/yyyy",
                                )}`}</Text>
                              </Flex>
                            </Tab>
                            <Tab
                              maxW="219px"
                              color="#575553"
                              _selected={{
                                border: "1px solid #DDEBEA",
                                borderBottom: "4px solid #169499",
                                color: "#169499",
                                fontWeight: 700,
                                bgColor: "#16949908",
                              }}
                            >
                              <Flex flexDirection="column" alignItems="center">
                                <Text fontSize="16px">Filtros Preenchidos</Text>
                                <Text
                                  fontSize="13px"
                                  fontWeight={300}
                                  color="#6A6A6AD9"
                                >
                                  Para Geração do Relatório
                                </Text>
                              </Flex>
                            </Tab>
                          </Flex>

                          <Button
                            type="button"
                            translate="no"
                            color="#FFF"
                            backgroundColor="#169499"
                            fontWeight={700}
                            fontSize="15px"
                            lineHeight="21.6px"
                            _hover={{ bg: "#169499" }}
                            w={"100%"}
                            maxW={"145px"}
                            onClick={() => {
                              sessionStorage.removeItem(entidade + "-filter");
                              sessionStorage.removeItem("servico-passou");

                              setPassedTabs([0]);
                              setActiveTabIndex(0);
                              setInnerTabIndex(0);

                              clearAllFieldsWithSetFieldValue(props);
                              props.setFieldValue("activeTabIndex", 0);

                              setMostrarTabela(false);
                            }}
                          >
                            Nova Pesquisa
                          </Button>
                        </TabList>

                        {children}
                      </Tabs>
                    </TabPanel>
                  </TabPanels>
                </Flex>
              </Box>
            </Tabs>
          </Form>
        )}
      </Formik>
    </Flex>
  ) : (
    <Box mt={2}>
      <SemRegistros buttonVisible={true} half />
    </Box>
  );
};
